import { Box, Stack, TextField, IconButton, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DialogTitle from 'components/dialog/DialogTitle';
import { useAppDispatch } from 'store';
import { showMessage } from 'store/message/messageSlice';
import { closeDialog } from 'store/dialog/dialogSlice';

interface ExternalLinkDialogProps {
  projectId: string;
}

function ExternalLinkDialog({ projectId }: ExternalLinkDialogProps): JSX.Element {
  const dispatch = useAppDispatch();

  const baseUrl = window.location.origin;
  const externalUrl = `${baseUrl}/external/register/${projectId}`;

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(externalUrl)
      .then(() => {
        dispatch(showMessage({ message: '링크가 클립보드에 복사되었습니다.', variant: 'success' }));
      })
      .catch(() => {
        dispatch(showMessage({ message: '링크 복사에 실패했습니다.', variant: 'error' }));
      });
  };

  const handleOpenInNewTab = () => {
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClose = () => {
    dispatch(closeDialog());
  };

  return (
    <Box sx={{ p: 2, minWidth: { xs: 300, sm: 500 } }}>
      <DialogTitle onClose={handleClose}>외부 등록 링크</DialogTitle>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          아래 링크를 통해 외부 사용자가 직접 등록하고 테스트를 진행할 수 있습니다.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            value={externalUrl}
            InputProps={{
              readOnly: true,
              sx: { fontSize: '0.9rem' },
            }}
            variant="outlined"
            size="small"
          />

          <IconButton
            onClick={handleCopyLink}
            color="primary"
            title="링크 복사"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            <ContentCopyIcon />
          </IconButton>

          <IconButton
            onClick={handleOpenInNewTab}
            color="secondary"
            title="새 창에서 열기"
            sx={{
              bgcolor: 'secondary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'secondary.dark',
              },
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <ContentCopyIcon sx={{ fontSize: 16, mr: 0.5 }} />
            복사
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <OpenInNewIcon sx={{ fontSize: 16, mr: 0.5 }} />새 창에서 열기
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default ExternalLinkDialog;
